import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MousePointer2 } from 'lucide-react';
import { heroData } from '../../data/mockData';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => {
  const canvasRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(heroData.logo);
  const [content, setContent] = useState(null);

  // Fetch logo and homepage content
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, contentRes] = await Promise.all([
          axios.get(`${API_URL}/api/site-settings`),
          axios.get(`${API_URL}/api/homepage-content`)
        ]);
        if (settingsRes.data?.logo_url) {
          setLogoUrl(settingsRes.data.logo_url);
        }
        if (contentRes.data) {
          setContent(contentRes.data);
        }
      } catch (err) {
        console.log('Using default content');
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let stars = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };

    const initStars = () => {
      stars = [];
      const numStars = 200;
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2,
          speed: Math.random() * 0.5 + 0.1,
          opacity: Math.random() * 0.8 + 0.2
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      stars.forEach((star) => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();

        star.y += star.speed;
        star.opacity += (Math.random() - 0.5) * 0.02;
        star.opacity = Math.max(0.1, Math.min(1, star.opacity));

        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Use API content or fallback to mockData
  const title = content?.hero_title || heroData.title;
  const subtitle = content?.hero_subtitle || heroData.subtitle;
  const tagline = content?.hero_tagline || heroData.tagline;
  const quote = content?.hero_quote || heroData.quote;
  const button1Text = content?.hero_button1_text || "Discover His Legacy";
  const button1Link = content?.hero_button1_link || "/remembering-leonard";
  const button2Text = content?.hero_button2_text || "Support the Foundation";
  const button2Link = content?.hero_button2_link || "/donate";

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0b]">
      {/* Starfield Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0b]/80 z-10" />

      {/* Content */}
      <div className="relative z-20 text-center px-4 max-w-4xl mx-auto pt-20">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-52 h-52 md:w-60 md:h-60 mx-auto rounded-full overflow-hidden ring-1 ring-white/90 shadow-2xl">
            <img
              src={logoUrl}
              alt="Nimoy-Knight Foundation Logo"
              className="w-full h-full object-cover scale-[1.15]"
              loading="eager"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-5xl md:text-7xl font-bold mb-6">
          <span className="text-amber-500">{title}</span>
          <br />
          <span className="text-white">{subtitle}</span>
        </h1>

        {/* Tagline */}
        <p className="text-gray-300 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
          {tagline}
        </p>

        {/* Quote */}
        <p className="text-amber-500/80 italic text-base md:text-lg mb-10 max-w-2xl mx-auto">
          "{quote}"
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={button1Link}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
          >
            {button1Text}
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to={button2Link}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-transparent border-2 border-gray-600 hover:border-gray-400 text-white font-semibold rounded-full transition-all hover:scale-105"
          >
            <MousePointer2 className="w-5 h-5" />
            {button2Text}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
